import type { PropsWithChildren } from 'react'

import { EmptyState } from '../EmptyState'
import { Upload, type IUploadProps } from '../Upload'
import { type IFileListContainerProps } from '../Upload/components/FileListContainer/FileListContainer'
import { FileListContainerStyled } from '../Upload/components/FileListContainer/styles/FileListContainerStyled'
import { UploadContainerStyled } from '../Upload/components/UploadContainer/styles/UploadContainerStyled'
import { type IUploadContainerProps } from '../Upload/components/UploadContainer/UploadContainer'
import { UploadDragDropWrapper } from '../Upload/components/UploadDragDropWrapper'
import { type IUploadDragDropWrapperProps } from '../Upload/components/UploadDragDropWrapper/UploadDragDropWrapper'
import { type IUploadEmptyStateProps } from '../Upload/components/UploadEmptyState/UploadEmptyState'
import { type IUploadTextProps } from '../Upload/components/UploadText/UploadText'
import { Text } from '@ui/elements/Text'

const defaultStrings = {
  fileSizeInfo: 'max file size',
  uploadInfo: 'No file attached',
  emptyStateText: 'No file attached',
  uploadButtonText: 'Upload',
}

export interface ISingleUploadProps extends Omit<IUploadProps, 'strings'> {
  strings?: Partial<typeof defaultStrings> & IUploadProps['strings']
}

const UploadText = (props: IUploadTextProps) => {
  const {
    commonProps: { strings },
    innerProps: { maxFileSize },
  } = props

  return (
    <Text>
      {strings?.uploadInfo} ({strings?.fileSizeInfo} {maxFileSize})
    </Text>
  )
}

const UploadContainer = (props: PropsWithChildren<IUploadContainerProps>) => {
  const {
    commonProps: { files, isReadonly },
    children,
  } = props

  if (files.length > 0 || isReadonly) return null

  return <UploadContainerStyled>{children}</UploadContainerStyled>
}

const FileListContainer = (
  props: PropsWithChildren<IFileListContainerProps>
) => {
  const {
    commonProps: { files, isReadonly },
    children,
  } = props

  if (files.length <= 0 && !isReadonly) return null

  return <FileListContainerStyled>{children}</FileListContainerStyled>
}

const UploadEmptyState = (props: IUploadEmptyStateProps) => {
  const {
    commonProps: { strings },
  } = props

  return <EmptyState hideImage small header={strings?.emptyStateText} />
}

const SingleUploadDragDropWrapper = (props: IUploadDragDropWrapperProps) => {
  return <UploadDragDropWrapper hideIcon {...props} />
}

export const SingleUpload = ({
  strings,
  ...otherProps
}: ISingleUploadProps) => {
  const extendedStrings = {
    ...defaultStrings,
    ...strings,
  }

  return (
    <Upload
      components={{
        FileListContainer,
        UploadContainer,
        UploadText,
        UploadEmptyState,
        UploadDragDropWrapper: SingleUploadDragDropWrapper,
      }}
      multiple={false}
      replaceFilelist
      strings={extendedStrings}
      {...otherProps}
    />
  )
}
